import { FC, FormEvent, useEffect, useState } from "react";
import { Button, Modal, Text } from "rizzui";
import { toast } from "sonner";
import { HeadquarterService } from "../../../services/headquarters/headquarterService";
import { MdOutlineCloudUpload } from "react-icons/md";
import { HiOutlineDownload } from "react-icons/hi";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import CustomSelect from "../../../components/forms/CustomSelect";

interface HeadquarterImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
}

type ImportSummary = {
    message: string;
    skipped: string[];
};

const HeadquarterImportModal: FC<HeadquarterImportModalProps> = ({ isOpen, onClose, refetch }) => {

    // STATES

    const [file, setFile] = useState<File | null>(null);
    const [company, setCompany] = useState<string>("");
    const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // HOOKS

    const service = new HeadquarterService();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege();
    const { getCompaniesList } = useCompanies();


    // FUNCTIONS

    // ----------------------------------------------------------------------------------------
    /**
     * @ES CIERRA EL MODAL
     * @EN CLOSE MODAL
     */
    // ----------------------------------------------------------------------------------------
    const handleClose = () => {
        setImportSummary(null);
        setFile(null);
        setCompany("");
        onClose();
    };
    // ----------------------------------------------------------------------------------------

    // ----------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL CAMBIO DEL ARCHIVO
     * @EN HANDLE FILE CHANGE
     */
    // ----------------------------------------------------------------------------------------
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
        }
    };
    // ----------------------------------------------------------------------------------------

    // ----------------------------------------------------------------------------------------
    /**
     * @ES MANEJA EL SUBMIT DEL FORMULARIO
     * @EN HANDLE SUBMIT
     */
    // ----------------------------------------------------------------------------------------
    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();

        if (!file) {
            toast.error("Por favor selecciona un archivo");
            return;
        }

        if (userCan("list", "company") && !company) {
            toast.error("Selecciona una compañía");
            return;
        }

        setIsSubmitting(true);
        const formData = new FormData();
        formData.append("file", file);
        if (company) {
            formData.append("company", company);
        }

        try {
            const response = await (await service.importExcel(formData)).getResponseData();
            if (response.success) {
                const skipped: string[] = Array.isArray(response.data?.skipped_rows)
                    ? response.data.skipped_rows
                    : [];
                setImportSummary({
                    message: response.message ?? "Importación completada",
                    skipped,
                });
                refetch();
                toast.success(
                    skipped.length > 0
                        ? "Importación completada. El detalle de filas omitidas está en esta ventana."
                        : "Importación completada.",
                    { duration: 4000 }
                );
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSubmitting(false);
        }
    };
    // ----------------------------------------------------------------------------------------

    // ----------------------------------------------------------------------------------------
    /**
     * @ES COPIA EL LISTADO DE FILAS OMITIDAS
     * @EN COPY SKIPPED LIST
     */
    // ----------------------------------------------------------------------------------------
    const copySkippedList = async () => {
        if (!importSummary?.skipped.length) return;
        const text = ["Filas ignoradas:", ...importSummary.skipped.map((line) => `• ${line}`)].join("\n");
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Listado copiado al portapapeles");
        } catch {
            toast.error("No se pudo copiar al portapapeles");
        }
    };
    // ----------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        if (isOpen) {
            setImportSummary(null);
        }
    }, [isOpen]);

    // RENDER

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <div className="p-6">
                {importSummary ? (
                    <>
                        <Text className="text-xl font-semibold text-green-700 dark:text-green-400">
                            Resultado de la importación
                        </Text>
                        <Text className="text-gray-700 dark:text-gray-300 mt-3 whitespace-pre-line">
                            {importSummary.message}
                        </Text>
                        <div className="mt-5">
                            <Text className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                                Filas ignoradas (no importadas) y motivo
                            </Text>
                            {importSummary.skipped.length === 0 ? (
                                <Text className="text-sm text-gray-500">No hubo filas omitidas.</Text>
                            ) : (
                                <>
                                    <pre className="max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-3 text-left text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 whitespace-pre-wrap font-sans">
                                        {importSummary.skipped.map((line) => `• ${line}`).join("\n")}
                                    </pre>
                                    <Button variant="outline" size="sm" className="mt-3" type="button" onClick={copySkippedList}>
                                        Copiar listado
                                    </Button>
                                </>
                            )}
                        </div>
                        <div className="mt-6 flex justify-end">
                            <Button type="button" onClick={handleClose}>Cerrar</Button>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="mb-6">
                            <Text className="text-xl font-semibold">Importar Sedes desde Excel</Text>
                            <Text className="text-gray-500 mt-2">
                                Cada fila debe incluir el CIF del proveedor (ya existente). Puedes indicar la ruta en la columna RUTA.
                                Un mismo CIF puede tener varias sedes (filas distintas).
                            </Text>
                            <div className="mt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-2"
                                    onClick={() => {
                                        const url = `${process.env.REACT_APP_API_PUBLIC_URL}/excel-template/headquarter`;
                                        window.open(url, "_blank");
                                    }}
                                >
                                    <HiOutlineDownload className="w-4 h-4" />
                                    Descargar Plantilla Excel
                                </Button>
                            </div>
                        </div>

                        {userCan("list", "company") && (
                            <div className="mb-6">
                                <CustomSelect
                                    isSearchable
                                    id="company"
                                    label="Compañía"
                                    options={getCompaniesList() || []}
                                    value={getCompaniesList().find((item: any) => item.value === company) ?? null}
                                    onChange={(value: any) => setCompany(value?.value ?? "")}
                                    isClearable={true}
                                    required={true}
                                />
                            </div>
                        )}

                        <div className="mb-6">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer relative">
                                <input
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                                <div className="flex flex-col items-center justify-center pointer-events-none">
                                    <MdOutlineCloudUpload className="w-12 h-12 text-gray-400 mb-2" />
                                    <Text className="text-gray-600 font-medium">
                                        {file ? file.name : "Haz clic o arrastra un archivo aquí"}
                                    </Text>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={isSubmitting} disabled={!file || (userCan("list", "company") && !company)}>
                                Importar
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </Modal>
    );
};

export default HeadquarterImportModal;
